import {
  Minus,
  Plus,
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  MoreHorizontal,
  RemoveFormatting,
  Underline,
} from "lucide-react";
import { useValue } from "@tldraw/state-react";
import type { Editor } from "./canvas";
import type { TLTextShape } from "@tldraw/tlschema";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import {
  clearTextFormatting,
  toggleTextMark,
  toggleTextList,
  textHasMark,
  textIsList,
  textColorValues,
  withoutTextColors,
  textFontSize,
  textLineHeight,
} from "./text-format";

import { TextStyleSelect } from "./TextStyleSelect";
import { RichTextLinkControl } from "./RichTextLinkControl";

function TextNumberInput({
  label,
  value,
  min,
  max,
  step = 1,
  compact = false,
  onCommit,
  onCancel,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  compact?: boolean;
  onCommit: (value: number) => void;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const skipBlur = useRef(false);
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const number = Number(draft);
    if (Number.isFinite(number) && number >= min && number <= max) {
      if (number !== value) onCommit(number);
      else onCancel?.();
    } else {
      setDraft(String(value));
      onCancel?.();
    }
  };
  return (
    <label
      className={`text-number-control${compact ? " text-number-primary" : ""}`}
      title={`${label} for this text box`}
    >
      <span className={compact ? "sr-only" : undefined}>{label}</span>
      <input
        aria-label={label}
        type="number"
        min={min}
        max={max}
        step={step}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (skipBlur.current) {
            skipBlur.current = false;
            onCancel?.();
          } else commit();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            skipBlur.current = true;
            setDraft(String(value));
            event.currentTarget.blur();
          }
        }}
      />
      {compact && (
        <span aria-hidden="true" className="text-number-unit">
          px
        </span>
      )}
    </label>
  );
}

const textColors = [
  ["black", "Charcoal"],
  ["grey", "Grey"],
  ["red", "Red"],
  ["blue", "Blue"],
  ["green", "Green"],
  ["violet", "Violet"],
  ["yellow", "Yellow"],
  ["white", "White"],
] as const;

export function TextFormatting({
  editor,
  shape,
}: {
  editor: Editor;
  shape: TLTextShape;
}) {
  const rich = useValue(
    "active text editor",
    () => editor.getRichTextEditor(),
    [editor],
  );
  const editing = useValue(
    "editing text",
    () => editor.getEditingShapeId() === shape.id,
    [editor, shape.id],
  );
  // Cursor moves and pending marks do not change the saved shape. Subscribe to
  // the rich editor too, so the toolbar reflects the next characters to be typed.
  const richState = useSyncExternalStore(
    useCallback(
      (onChange) => {
        rich?.on("transaction", onChange);
        return () => {
          rich?.off("transaction", onChange);
        };
      },
      [rich],
    ),
    () => rich?.state ?? null,
  );
  const [moreOpen, setMoreOpen] = useState(false);
  const moreTrigger = useRef<HTMLButtonElement>(null);
  const morePanel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!moreOpen) return;
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !moreTrigger.current?.contains(target) &&
        !morePanel.current?.contains(target)
      )
        setMoreOpen(false);
    };
    const close = () => setMoreOpen(false);
    window.addEventListener("pointerdown", closeOutside, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("pointerdown", closeOutside, true);
      window.removeEventListener("resize", close);
    };
  }, [moreOpen]);
  const resumeEditing = () => {
    if (editing && rich && !rich.isDestroyed) rich.view.focus();
    else editor.focus();
  };
  const update = (
    props: Partial<TLTextShape["props"]>,
    meta?: Record<string, number | null>,
  ) => {
    const current = editor.getShape<TLTextShape>(shape.id);
    if (!current || current.isLocked || editor.getIsReadonly()) return;
    editor.markHistoryStoppingPoint("Format text");
    editor.updateShape({
      id: shape.id,
      type: "text",
      props: meta
        ? { ...props, richText: { ...current.props.richText } }
        : props,
      ...(meta ? { meta: { ...current.meta, ...meta } } : {}),
    });
    resumeEditing();
  };
  const mark = (kind: "bold" | "italic" | "underline") => {
    if (editing && rich) {
      rich.commands.toggleMark(kind);
      resumeEditing();
    } else update({ richText: toggleTextMark(shape.props.richText, kind) });
  };
  const active = (kind: string) =>
    editing && rich
      ? rich.isActive(kind)
      : kind === "bulletList" || kind === "orderedList"
        ? textIsList(shape.props.richText, kind)
        : textHasMark(shape.props.richText, kind);
  // Theme colour names resolve to their solid value; anything else is already a colour.
  const resolveColor = (value: string) =>
    (
      editor.getCurrentTheme().colors[editor.getColorMode()] as unknown as Record<
        string,
        { solid?: string } | undefined
      >
    )[value]?.solid ?? value;
  const baseColor = resolveColor(shape.props.color);
  const colors =
    editing && rich && richState
      ? richState.selection.empty
        ? [rich.getAttributes("textStyle").color || baseColor]
        : textColorValues(
            {
              type: "doc",
              content: richState.selection.content().content.toJSON(),
            },
            baseColor,
          )
      : textColorValues(shape.props.richText, baseColor);
  const color = colors.length === 1 ? colors[0] : null;
  const colorValue =
    color === null
      ? "mixed"
      : (textColors.find(([value]) => resolveColor(value) === color)?.[0] ??
        "custom");

  return (
    <div
      className="text-formatting"
      role="toolbar"
      aria-label="Text formatting"
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => {
        if (event.target instanceof Element && event.target.closest("button")) {
          const focused = document.activeElement;
          if (
            focused instanceof HTMLInputElement &&
            focused.closest(".text-number-control")
          )
            focused.blur();
          event.preventDefault();
        }
      }}
    >
      <TextStyleSelect
        className="text-font"
        label="Font"
        title="Font for this text box"
        value={shape.props.font}
        onDismiss={resumeEditing}
        onChange={(value) =>
          update({ font: value as TLTextShape["props"]["font"] })
        }
        options={[
          { value: "sans", label: "Sans serif" },
          { value: "serif", label: "Serif" },
          { value: "mono", label: "Monospace" },
          { value: "draw", label: "Handwritten" },
        ]}
      />
      <div className="text-size-stepper" role="group" aria-label="Text size">
        <button
          aria-label="Decrease font size"
          title="Decrease font size"
          disabled={
            textFontSize(shape, editor.getCurrentTheme().fontSize) <= 10
          }
          onClick={() =>
            update(
              {},
              {
                calqueTextSize: Math.max(
                  10,
                  textFontSize(shape, editor.getCurrentTheme().fontSize) - 1,
                ),
              },
            )
          }
        >
          <Minus size={16} />
        </button>
        <TextNumberInput
          label="Font size"
          compact
          value={textFontSize(shape, editor.getCurrentTheme().fontSize)}
          min={10}
          max={96}
          onCommit={(value) => update({}, { calqueTextSize: value })}
          onCancel={resumeEditing}
        />
        <button
          aria-label="Increase font size"
          title="Increase font size"
          disabled={
            textFontSize(shape, editor.getCurrentTheme().fontSize) >= 96
          }
          onClick={() =>
            update(
              {},
              {
                calqueTextSize: Math.min(
                  96,
                  textFontSize(shape, editor.getCurrentTheme().fontSize) + 1,
                ),
              },
            )
          }
        >
          <Plus size={16} />
        </button>
      </div>
      <span className="text-toolbar-divider" aria-hidden="true" />
      <button
        aria-label="Bold"
        title="Bold"
        aria-pressed={!!active("bold")}
        onClick={() => mark("bold")}
      >
        <Bold size={17} />
      </button>
      <button
        aria-label="Italic"
        title="Italic"
        aria-pressed={!!active("italic")}
        onClick={() => mark("italic")}
      >
        <Italic size={17} />
      </button>
      <TextStyleSelect
        className="text-color-control"
        label="Text colour"
        title={
          editing
            ? "Colour for selected text or what you type next"
            : "Colour for this text box"
        }
        decoration={
          <span
            className="text-color-indicator"
            aria-hidden="true"
            style={{ borderBottomColor: color ?? "transparent" }}
          >
            A
          </span>
        }
        value={colorValue}
        onDismiss={resumeEditing}
        onChange={(next) => {
          const value = next as TLTextShape["props"]["color"];
          if (editing && rich) {
            rich.commands.setColor(resolveColor(value));
            resumeEditing();
          } else
            update({
              color: value,
              richText: withoutTextColors(shape.props.richText),
            });
        }}
        options={textColors.map(([value, label]) => ({
          value,
          label,
          color: resolveColor(value),
        }))}
      />
      {editing && <RichTextLinkControl rich={rich} onDismiss={resumeEditing} />}
      <button
        ref={moreTrigger}
        type="button"
        className="text-more-trigger"
        aria-label="More text formatting"
        title="More text formatting"
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        onClick={() => setMoreOpen((value) => !value)}
      >
        <MoreHorizontal size={18} />
      </button>
      {moreOpen &&
        moreTrigger.current &&
        createPortal(
          <div
            ref={morePanel}
            role="dialog"
            aria-label="More text formatting"
            className="calque-format-panel floating-surface"
            style={{
              left: Math.max(
                8,
                Math.min(
                  window.innerWidth - 260,
                  moreTrigger.current.getBoundingClientRect().left,
                ),
              ),
              top: Math.max(
                8,
                Math.min(
                  window.innerHeight - 288,
                  moreTrigger.current.getBoundingClientRect().bottom + 6,
                ),
              ),
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => {
              if (
                event.target instanceof Element &&
                event.target.closest("button")
              )
                event.preventDefault();
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Escape") {
                event.preventDefault();
                setMoreOpen(false);
                resumeEditing();
              }
            }}
          >
            <strong className="calque-format-heading">Paragraph</strong>
            <TextNumberInput
              label="Line spacing"
              value={textLineHeight(shape, editor.getCurrentTheme().lineHeight)}
              min={1}
              max={2.5}
              step={0.05}
              onCommit={(value) => update({}, { calqueLineHeight: value })}
              onCancel={resumeEditing}
            />
            <div
              className="calque-format-row"
              role="group"
              aria-label="Text alignment"
            >
              {(
                [
                  ["start", AlignLeft, "Align left"],
                  ["middle", AlignCenter, "Align centre"],
                  ["end", AlignRight, "Align right"],
                ] as const
              ).map(([alignment, Icon, label]) => (
                <button
                  key={alignment}
                  aria-label={label}
                  title={label}
                  aria-pressed={shape.props.textAlign === alignment}
                  onClick={() => update({ textAlign: alignment })}
                >
                  <Icon size={17} />
                </button>
              ))}
            </div>
            <div className="calque-format-row" role="group" aria-label="Lists">
              <button
                aria-label="Bullet list"
                title="Bullet list"
                aria-pressed={!!active("bulletList")}
                onClick={() => {
                  if (editing && rich) {
                    rich.commands.toggleBulletList();
                    resumeEditing();
                  } else
                    update({ richText: toggleTextList(shape.props.richText) });
                }}
              >
                <List size={17} />
                <span>Bullets</span>
              </button>
              <button
                aria-label="Numbered list"
                title="Numbered list"
                aria-pressed={!!active("orderedList")}
                onClick={() => {
                  if (editing && rich) {
                    rich.commands.toggleOrderedList();
                    resumeEditing();
                  } else
                    update({
                      richText: toggleTextList(
                        shape.props.richText,
                        "orderedList",
                      ),
                    });
                }}
              >
                <ListOrdered size={17} />
                <span>Numbers</span>
              </button>
            </div>
            <div className="calque-format-actions">
              <button
                aria-label="Underline"
                title="Underline"
                aria-pressed={!!active("underline")}
                onClick={() => mark("underline")}
              >
                <Underline size={17} /> Underline
              </button>
              <button
                aria-label="Clear text formatting"
                title="Clear text formatting"
                onClick={() => {
                  if (editing && rich)
                    rich.chain().focus().unsetAllMarks().clearNodes().run();
                  else
                    update({
                      richText: clearTextFormatting(shape.props.richText),
                    });
                  resumeEditing();
                }}
              >
                <RemoveFormatting size={17} /> Clear formatting
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
